/**
 * API client for the Rivercity CRM.
 *
 * - Uses `/api/*` same-origin path (proxied by Pages Function to the Worker).
 * - Sends session cookies via `credentials: "include"`.
 * - Attaches the CSRF token on every mutation via `X-CSRF-Token`.
 * - Structured `ApiError` for consistent handling in components.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "/api"

let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

export function getCsrfToken(): string | null {
  return csrfToken
}

export type ApiErrorPayload = Record<string, unknown>

export class ApiError extends Error {
  public readonly status: number
  public readonly code: string
  public readonly fieldErrors?: Record<string, string>
  public readonly correlationId?: string
  /** Full parsed JSON body for extra fields (e.g. duplicates on 409). */
  public readonly payload?: ApiErrorPayload

  constructor(
    status: number,
    code: string,
    message: string,
    fieldErrors?: Record<string, string>,
    correlationId?: string,
    payload?: ApiErrorPayload
  ) {
    super(message)
    this.status = status
    this.code = code
    this.fieldErrors = fieldErrors
    this.correlationId = correlationId
    this.payload = payload
  }
}

type RequestOptions = RequestInit & { skipCsrf?: boolean }

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipCsrf, ...init } = options
  const method = (init.method ?? "GET").toUpperCase()
  const isMutation = !["GET", "HEAD", "OPTIONS"].includes(method)

  const headers = new Headers(init.headers)
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json")
  }
  if (isMutation && !skipCsrf && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken)
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  })

  const contentType = res.headers.get("content-type") ?? ""
  const isJson = contentType.includes("application/json")
  const body = isJson ? await res.json() : null

  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? "unknown_error",
      body?.message ?? res.statusText,
      body?.fieldErrors,
      body?.correlationId,
      body ?? undefined
    )
  }
  return body as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: "POST", body: JSON.stringify(data ?? {}), ...opts }),
  put: <T>(path: string, data?: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(data ?? {}), ...opts }),
  patch: <T>(path: string, data?: unknown, opts?: RequestOptions) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(data ?? {}), ...opts }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { method: "DELETE", ...opts }),
}

// ============================================================
// Auth API
// ============================================================
export type Role = "admin" | "manager" | "broker" | "assistant" | "compliance"

export type PublicUser = {
  id: string
  email: string
  fullName: string
  role: Role
  organisationId: string
  timezone: string
  totpEnrolled: boolean
}

export type LoginResponse =
  | {
      requiresEnrol: true
      enrolId: string
      totpSecret: string
      totpUri: string
    }
  | {
      requires2fa: true
      challengeId: string
    }

export type Verify2faResponse = {
  ok: true
  csrf: string
  user: PublicUser
}

export type ConfirmEnrolResponse = {
  ok: true
  csrf: string
  user: PublicUser
  recoveryCodes: string[]
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>("/auth/login", { email, password }, { skipCsrf: true }),

  verify2fa: (challengeId: string, code: string) =>
    api.post<Verify2faResponse>("/auth/verify-2fa", { challengeId, code }, { skipCsrf: true }),

  verifyRecovery: (challengeId: string, code: string) =>
    api.post<Verify2faResponse & { recoveryCodesRemaining: number }>(
      "/auth/verify-recovery",
      { challengeId, code },
      { skipCsrf: true }
    ),

  confirmEnrolTotp: (enrolId: string, code: string) =>
    api.post<ConfirmEnrolResponse>(
      "/auth/confirm-enrol-totp",
      { enrolId, code },
      { skipCsrf: true }
    ),

  logout: () => api.post<{ ok: true }>("/auth/logout", {}, { skipCsrf: true }),

  forgotPasswordStart: (email: string) =>
    api.post<{
      ok: true
      message: string
      resetId: string
    }>("/auth/forgot-password/start", { email }, { skipCsrf: true }),

  forgotPasswordComplete: (resetId: string, code: string, newPassword: string) =>
    api.post<{ ok: true }>(
      "/auth/forgot-password/complete",
      { resetId, code, newPassword },
      { skipCsrf: true }
    ),

  me: () => api.get<{ user: PublicUser | null; csrf?: string }>("/auth/me"),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: true }>("/auth/change-password", { currentPassword, newPassword }),

  regenerateRecoveryCodes: () => api.post<{ codes: string[] }>("/auth/regenerate-recovery-codes"),

  rotateSession: () => api.post<{ ok: true; csrf: string }>("/auth/rotate-session"),
}

// ============================================================
// Users API (for owner dropdowns)
// ============================================================
export type UserOption = {
  id: string
  fullName: string
  email: string
  role: Role
  isActive: boolean
}

export type AdminUser = {
  id: string
  fullName: string
  email: string
  phone: string | null
  role: Role
  isActive: boolean
  timezone: string
  totpConfirmedAt: number | null
  lastLoginAt: number | null
  passwordChangedAt: number | null
  createdAt: number
}

export type CreateUserInput = {
  email: string
  fullName: string
  phone?: string | null
  role: Role
  password: string
  timezone?: string
}

export type UpdateUserInput = {
  fullName?: string
  email?: string
  phone?: string | null
  role?: Role
  isActive?: boolean
  timezone?: string
}

// ============================================================
// Notifications API
// ============================================================
export type NotificationKind =
  | "task_assigned"
  | "task_due_soon"
  | "task_overdue"
  | "deal_stage"
  | "contact_assigned"
  | "automation_failed"
  | "system"

export type Notification = {
  id: string
  kind: NotificationKind
  title: string
  body: string | null
  entityType: string | null
  entityId: string | null
  href: string | null
  readAt: number | null
  createdAt: number
}

export const notificationsApi = {
  list: (params: { unread?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams()
    if (params.unread) q.set("unread", "1")
    if (params.limit) q.set("limit", String(params.limit))
    const suffix = q.toString() ? `?${q}` : ""
    return api.get<{ items: Notification[] }>(`/notifications${suffix}`)
  },
  unreadCount: () => api.get<{ unread: number }>("/notifications/unread-count"),
  markRead: (id: string) =>
    api.post<{ ok: true }>(`/notifications/${encodeURIComponent(id)}/read`),
  markAllRead: () => api.post<{ ok: true }>("/notifications/read-all"),
}

// ============================================================
// Users API (for owner dropdowns)
// (Kept below to avoid renumbering; original block above.)
// ============================================================
export const usersApi = {
  list: () => api.get<{ users: UserOption[] }>("/users"),
  listAll: () => api.get<{ users: AdminUser[] }>("/users/admin"),
  create: (input: CreateUserInput) =>
    api.post<{ ok: true; user: UserOption & { phone: string | null } }>("/users", input),
  update: (id: string, patch: UpdateUserInput) =>
    api.patch<{ ok: true }>(`/users/${id}`, patch),
  resetPassword: (id: string, newPassword: string, clearTotp = false) =>
    api.post<{ ok: true }>(`/users/${id}/reset-password`, { newPassword, clearTotp }),
  deactivate: (id: string) => api.delete<{ ok: true }>(`/users/${id}`),
}

// ============================================================
// Contacts API
// ============================================================
export type ContactStatus = "new" | "contacted" | "active" | "client" | "inactive"
export type AuState = "NSW" | "VIC" | "QLD" | "WA" | "SA" | "TAS" | "ACT" | "NT"
export type BusinessStructure = "sole_trader" | "partnership" | "company" | "trust" | "individual"
export type ContactMethod = "phone" | "email" | "sms"

export type ContactOpenDeal = {
  contactId: string
  id: string
  dealName: string | null
  status: DealStatus
  pipelineStage: PipelineStage
  naf: number | null
  loanAmount: number | null
}

export type ContactListItem = {
  id: string
  firstName: string
  lastName: string
  businessName: string | null
  abn: string | null
  email: string | null
  mobile: string | null
  status: ContactStatus
  industry: string | null
  addressSuburb: string | null
  addressState: AuState | null
  isFavourite: boolean
  ownerId: string | null
  ownerName: string | null
  lastContactedAt: number | null
  createdAt: number
  updatedAt: number
  totalDeals: number
  totalFunded: number
  source: string | null
  openDeals: ContactOpenDeal[]
}

export type Contact = {
  id: string
  organisationId: string
  ownerId: string | null
  ownerName: string | null
  status: ContactStatus
  businessName: string | null
  abn: string | null
  acn: string | null
  businessStructure: BusinessStructure | null
  industry: string | null
  annualTurnover: string | null
  employeesCount: number | null
  firstName: string
  lastName: string
  title: string | null
  email: string | null
  mobile: string | null
  businessPhone: string | null
  dateOfBirth: string | null
  occupation: string | null
  coApplicantTitle: string | null
  coApplicantFirstName: string | null
  coApplicantLastName: string | null
  coApplicantEmail: string | null
  coApplicantMobile: string | null
  addressStreet: string | null
  addressSuburb: string | null
  addressState: AuState | null
  addressPostcode: string | null
  preferredContactMethod: ContactMethod | null
  bestContactTime: string | null
  isFavourite: boolean
  marketingConsent: boolean
  emailOptOut: boolean
  smsOptOut: boolean
  // Phase 1 preference fields (present in the DB, returned by GET /contacts/:id)
  emailMarketingStatus: EmailMarketingStatus
  smsMarketingStatus: SmsMarketingStatus
  operationalEmailStatus: OperationalStatus
  operationalSmsStatus: OperationalStatus
  doNotContactStatus: boolean
  emailConsentSource: string | null
  smsConsentSource: string | null
  emailConsentDate: number | null
  smsConsentDate: number | null
  emailUnsubscribedDate: number | null
  smsUnsubscribedDate: number | null
  unsubscribeReason: string | null
  unsubscribeSource: string | null
  communicationPreferencesUpdatedAt: number | null
  communicationPreferencesUpdatedBy: string | null
  source: string | null
  notes: string | null
  financeType: string | null
  website: string | null
  clientSince: number | null
  lastContactedAt: number | null
  totalDeals: number
  totalFunded: number
  createdAt: number
  updatedAt: number
  createdBy: string | null
  tags: string[]
}

export type EmailMarketingStatus =
  | "subscribed"
  | "unsubscribed"
  | "bounced"
  | "complained"
  | "unknown"
export type SmsMarketingStatus =
  | "subscribed"
  | "unsubscribed"
  | "invalid"
  | "complained"
  | "unknown"
export type OperationalStatus = "allowed" | "restricted"

export const CONSENT_SOURCES = [
  "website enquiry",
  "client phone request",
  "client email request",
  "existing client relationship",
  "finance application",
  "manual staff entry",
  "imported",
  "unknown",
] as const
export type ConsentSource = (typeof CONSENT_SOURCES)[number]

export type ContactPreferences = {
  emailMarketingStatus: EmailMarketingStatus
  smsMarketingStatus: SmsMarketingStatus
  operationalEmailStatus: OperationalStatus
  operationalSmsStatus: OperationalStatus
  doNotContactStatus: boolean
  emailConsentSource: string | null
  smsConsentSource: string | null
  emailConsentDate: number | null
  smsConsentDate: number | null
  emailUnsubscribedDate: number | null
  smsUnsubscribedDate: number | null
  unsubscribeReason: string | null
  unsubscribeSource: string | null
  communicationPreferencesUpdatedAt: number | null
  communicationPreferencesUpdatedBy: string | null
  /** Import quiet-period timestamp (unix epoch). null = not paused. */
  automationPausedUntil: number | null
  importBatchId: string | null
}

export type PreferenceHistoryRow = {
  id: string
  fieldName: string
  previousValue: string | null
  newValue: string | null
  changeSource: string
  changeReason: string | null
  relatedMessageId: string | null
  relatedAutomationId: string | null
  inboundMessageText: string | null
  changedBy: string | null
  changedByName: string | null
  changedAt: number
}

export type PreferencesResponse = {
  preferences: ContactPreferences
  history: PreferenceHistoryRow[]
}

export type PreferenceAction =
  | "unsubscribe_marketing"
  | "resubscribe_marketing"
  | "set_dnc"
  | "clear_dnc"
  | "restrict_operational"
  | "allow_operational"
  | "clear_automation_pause"

export type PreferenceMutationInput = {
  action: PreferenceAction
  channel?: "email" | "sms" | "all"
  reason: string
  consentSource?: ConsentSource
}

export type ContactInput = {
  status?: ContactStatus
  businessName?: string | null
  abn?: string | null
  acn?: string | null
  businessStructure?: BusinessStructure | null
  industry?: string | null
  annualTurnover?: string | null
  employeesCount?: number | null
  firstName: string
  lastName: string
  title?: string | null
  email?: string | null
  mobile?: string | null
  businessPhone?: string | null
  dateOfBirth?: string | null
  occupation?: string | null
  coApplicantTitle?: string | null
  coApplicantFirstName?: string | null
  coApplicantLastName?: string | null
  coApplicantEmail?: string | null
  coApplicantMobile?: string | null
  addressStreet?: string | null
  addressSuburb?: string | null
  addressState?: AuState | null
  addressPostcode?: string | null
  preferredContactMethod?: ContactMethod | null
  bestContactTime?: string | null
  isFavourite?: boolean
  marketingConsent?: boolean
  emailOptOut?: boolean
  smsOptOut?: boolean
  source?: string | null
  notes?: string | null
  financeType?: string | null
  website?: string | null
  ownerId?: string | null
  tags?: string[]
}

export type AbnLookupResult =
  | {
      ok: true
      abn: string
      businessName: string
      entityType: string | null
      status: string | null
      state: string | null
    }
  | { ok: false; error: string }

export type ContactListParams = {
  q?: string
  status?: ContactStatus
  ownerId?: string
  source?: string
  page?: number
  pageSize?: number
}

export const contactsApi = {
  list: (params: ContactListParams = {}) => {
    const query = new URLSearchParams()
    if (params.q) query.set("q", params.q)
    if (params.status) query.set("status", params.status)
    if (params.ownerId) query.set("ownerId", params.ownerId)
    if (params.source) query.set("source", params.source)
    if (params.page) query.set("page", String(params.page))
    if (params.pageSize) query.set("pageSize", String(params.pageSize))
    const suffix = query.toString() ? `?${query}` : ""
    return api.get<{
      items: ContactListItem[]
      total: number
      page: number
      pageSize: number
      hasMore: boolean
    }>(`/contacts${suffix}`)
  },

  get: (id: string) => api.get<{ contact: Contact }>(`/contacts/${encodeURIComponent(id)}`),

  create: (data: ContactInput) => api.post<{ ok: true; id: string }>("/contacts", data),

  update: (id: string, data: Partial<ContactInput>) =>
    api.patch<{ ok: true }>(`/contacts/${encodeURIComponent(id)}`, data),

  remove: (id: string) => api.delete<{ ok: true }>(`/contacts/${encodeURIComponent(id)}`),

  assign: (id: string, ownerId: string | null) =>
    api.post<{ ok: true }>(`/contacts/${encodeURIComponent(id)}/assign`, { ownerId }),

  abnLookup: (abn: string) =>
    api.get<AbnLookupResult>(`/contacts/abn-lookup?abn=${encodeURIComponent(abn)}`),

  tags: () => api.get<{ tags: string[] }>("/contacts/tags"),

  sources: () => api.get<{ sources: string[] }>("/contacts/sources"),

  getPreferences: (id: string) =>
    api.get<PreferencesResponse>(`/contacts/${encodeURIComponent(id)}/preferences`),

  updatePreferences: (id: string, input: PreferenceMutationInput) =>
    api.patch<{
      ok: true
      changed: boolean
      changedFields: string[]
      /** How many active automation runs were cancelled for this opt-out. */
      cancelledRunCount?: number
    }>(`/contacts/${encodeURIComponent(id)}/preferences`, input),
}

// ============================================================
// Deals API
// ============================================================
export type FinanceType = "commercial" | "consumer"
export type DealStatus =
  | "new_lead"
  | "awaiting_docs"
  | "docs_received"
  | "approved"
  | "settled"
  | "closed"
  | "lost"
  | "on_hold"
export type PipelineStage =
  | "new_lead"
  | "day_one"
  | "day_two"
  | "day_three"
  | "general_followup"
  | "awaiting_docs"
  | "docs_received"
  | "approved"
  | "settled"
  | "closed"
export type Priority = "low" | "medium" | "high"

export type DealListItem = {
  id: string
  contactId: string
  ownerId: string | null
  dealName: string | null
  lender: string | null
  loanAmount: number | null
  naf: number | null
  amountRequired: number | null
  financeType: FinanceType
  financeCategory: string | null
  status: DealStatus
  pipelineStage: PipelineStage
  priority: Priority
  expectedCloseDate: string | null
  settledDate: string | null
  settledAmount: number | null
  createdAt: number
  updatedAt: number
  contactFirstName: string | null
  contactLastName: string | null
  contactBusinessName: string | null
  ownerName: string | null
}

export type DealPipelineCard = Pick<
  DealListItem,
  | "id"
  | "contactId"
  | "ownerId"
  | "dealName"
  | "loanAmount"
  | "naf"
  | "amountRequired"
  | "status"
  | "pipelineStage"
  | "priority"
  | "expectedCloseDate"
  | "financeType"
  | "financeCategory"
  | "updatedAt"
  | "contactFirstName"
  | "contactLastName"
  | "contactBusinessName"
  | "ownerName"
> & {
  lender: string | null
  lenderName: string | null
  lenderSlug: string | null
  lenderLogoUrl: string | null
}

export type SellerType = "dealer" | "private" | "auction"
export type RepaymentFrequency = "weekly" | "fortnightly" | "monthly"

export type DealInput = {
  contactId: string
  financeType?: FinanceType
  financeCategory?: string | null
  dealName?: string | null
  lender?: string | null
  lenderId?: string | null
  loanAmount?: number | null
  naf?: number | null
  balloon?: number | null
  termMonths?: number | null
  writingRate?: number | null
  commission?: number | null
  loanPurpose?: string | null
  status?: DealStatus
  pipelineStage?: PipelineStage
  priority?: Priority
  expectedCloseDate?: string | null
  sourceProvider?: string | null
  notes?: string | null
  ownerId?: string | null
  settledDate?: string | null
  settledAmount?: number | null
  termEndDate?: string | null
  // Stage 1 enquiry fields (migration 0025)
  coApplicantContactId?: string | null
  assetDescription?: string | null
  sellerType?: SellerType | null
  dealerName?: string | null
  purchasePrice?: number | null
  deposit?: number | null
  tradeValue?: number | null
  tradePayout?: number | null
  amountRequired?: number | null
  preferredBalloonPct?: number | null
  preferredRepaymentFrequency?: RepaymentFrequency | null
  leadSource?: string | null
}

export type Deal = DealInput & {
  id: string
  organisationId: string
  balloon: number | null
  termMonths: number | null
  writingRate: number | null
  commission: number | null
  settledDate: string | null
  settledAmount: number | null
  termEndDate: string | null
  closedReason: string | null
  contact: Contact | null
  ownerName: string | null
  lenderName: string | null
  lenderSlug: string | null
  lenderLogoUrl: string | null
  createdAt: number
  updatedAt: number
  createdBy: string | null
}

export type PipelineHistoryEntry = {
  id: string
  dealId: string
  fromStage: string | null
  toStage: string
  fromStatus: string | null
  toStatus: string | null
  changedBy: string | null
  notes: string | null
  createdAt: number
}

export type DealListParams = {
  q?: string
  status?: DealStatus
  pipelineStage?: PipelineStage
  contactId?: string
  ownerId?: string
  page?: number
  pageSize?: number
}

export const dealsApi = {
  list: (params: DealListParams = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") query.set(k, String(v))
    })
    const suffix = query.toString() ? `?${query}` : ""
    return api.get<{
      items: DealListItem[]
      total: number
      page: number
      pageSize: number
      hasMore: boolean
    }>(`/deals${suffix}`)
  },

  pipeline: () =>
    api.get<{
      board: Record<PipelineStage, DealPipelineCard[]>
      stages: readonly PipelineStage[]
    }>("/deals/pipeline"),

  leadSources: () =>
    api.get<{
      sources: Array<{ value: string; count: number; builtIn: boolean }>
    }>("/deals/lead-sources"),

  get: (id: string) =>
    api.get<{ deal: Deal; history: PipelineHistoryEntry[] }>(`/deals/${encodeURIComponent(id)}`),

  create: (data: DealInput) => api.post<{ ok: true; id: string }>("/deals", data),

  update: (id: string, data: Partial<DealInput>) =>
    api.patch<{ ok: true }>(`/deals/${encodeURIComponent(id)}`, data),

  move: (id: string, pipelineStage: PipelineStage, closedReason?: string) =>
    api.post<{
      ok: true
      pipelineStage: PipelineStage
      status: DealStatus
      /** True when the transition landed IN 'settled' state (from any other state). */
      transitionedIntoSettled?: boolean
      /** IDs of the 5 nurture automations to offer for enrol in the settled dialog. */
      settledEnrolAutomationIds?: string[]
    }>(
      `/deals/${encodeURIComponent(id)}/move`,
      { pipelineStage, closedReason: closedReason ?? null }
    ),

  remove: (id: string) => api.delete<{ ok: true }>(`/deals/${encodeURIComponent(id)}`),

  /**
   * Quick-close a deal as lost with a reason. Sets status=lost,
   * pipelineStage=closed, closedReason, and lostDate=today (enrols the deal
   * in days_since_deal_lost automations). Used by the "Close as fake / not
   * interested" quick action on contact + deal detail pages.
   */
  closeAsLost: (id: string, reason: string) =>
    api.post<{
      ok: true
      status: "lost"
      pipelineStage: "closed"
      closedReason: string
      lostDate: string
      alreadyLost?: boolean
    }>(`/deals/${encodeURIComponent(id)}/close-lost`, { reason }),
}

// ============================================================
// Leads API (web-sourced deals via /public/leads endpoint)
// ============================================================

export type LeadListItem = {
  id: string
  receivedAt: number
  source: string
  referenceCode: string | null
  reusedExistingDeal: boolean
  createdNewContact: boolean
  contactId: string
  dealId: string | null
  contactFirstName: string | null
  contactLastName: string | null
  contactEmail: string | null
  contactMobile: string | null
  contactBusinessName: string | null
  dealStatus: string | null
  dealPipelineStage: string | null
  dealNaf: number | null
  dealLoanAmount: number | null
  dealFinanceCategory: string | null
  ownerId: string | null
  ownerFullName: string | null
}

export const leadsApi = {
  list: (params: {
    source?: string
    assignedTo?: string
    filter?: "active" | "all"
    q?: string
    since?: string
    until?: string
    page?: number
    pageSize?: number
  } = {}) => {
    const search = new URLSearchParams()
    if (params.source) search.set("source", params.source)
    if (params.assignedTo) search.set("assignedTo", params.assignedTo)
    if (params.filter) search.set("filter", params.filter)
    if (params.q) search.set("q", params.q)
    if (params.since) search.set("since", params.since)
    if (params.until) search.set("until", params.until)
    if (params.page) search.set("page", String(params.page))
    if (params.pageSize) search.set("pageSize", String(params.pageSize))
    const qs = search.toString()
    return api.get<{
      ok: true
      page: number
      pageSize: number
      total: number
      leads: LeadListItem[]
    }>(`/leads${qs ? `?${qs}` : ""}`)
  },

  sources: () => api.get<{ ok: true; sources: string[] }>(`/leads/sources`),

  get: (id: string) =>
    api.get<{
      ok: true
      lead: {
        id: string
        organisationId: string
        contactId: string | null
        dealId: string | null
        source: string
        referenceCode: string | null
        reusedExistingDeal: boolean
        createdNewContact: boolean
        ipAddress: string | null
        userAgent: string | null
        referrer: string | null
        rawPayload: Record<string, unknown> | string
        accepted: boolean
        rejectionReason: string | null
        createdAt: number
      }
    }>(`/leads/${encodeURIComponent(id)}`),

  assign: (id: string, ownerId: string | null) =>
    api.patch<{ ok: true; dealId: string; ownerId: string | null }>(
      `/leads/${encodeURIComponent(id)}/assign`,
      { ownerId }
    ),
}

// ============================================================
// Tasks API
// ============================================================
export type TaskStatus = "pending" | "in_progress" | "completed" | "cancelled"
export type TaskView = "mine" | "today" | "overdue" | "upcoming" | "completed" | "all"

export type TaskListItem = {
  id: string
  title: string
  description: string | null
  priority: Priority
  status: TaskStatus
  dueAt: number | null
  completedAt: number | null
  source: string | null
  contactId: string | null
  dealId: string | null
  ownerId: string | null
  createdAt: number
  updatedAt: number
  contactFirstName: string | null
  contactLastName: string | null
  contactBusinessName: string | null
  dealName: string | null
  ownerName: string | null
}

export type TaskInput = {
  title: string
  description?: string | null
  contactId?: string | null
  dealId?: string | null
  ownerId?: string | null
  priority?: Priority
  status?: TaskStatus
  dueAt?: number | null
}

export type TaskListParams = {
  q?: string
  view?: TaskView
  status?: TaskStatus
  contactId?: string
  dealId?: string
  ownerId?: string
  page?: number
  pageSize?: number
}

export const tasksApi = {
  list: (params: TaskListParams = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") query.set(k, String(v))
    })
    const suffix = query.toString() ? `?${query}` : ""
    return api.get<{
      items: TaskListItem[]
      total: number
      page: number
      pageSize: number
      hasMore: boolean
    }>(`/tasks${suffix}`)
  },

  counts: () => api.get<{ overdue: number; today: number; upcoming: number }>("/tasks/counts"),

  create: (data: TaskInput) => api.post<{ ok: true; id: string }>("/tasks", data),

  update: (id: string, data: Partial<TaskInput>) =>
    api.patch<{ ok: true }>(`/tasks/${encodeURIComponent(id)}`, data),

  toggle: (id: string, completed: boolean) =>
    api.patch<{ ok: true }>(`/tasks/${encodeURIComponent(id)}`, {
      status: completed ? "completed" : "pending",
    }),

  remove: (id: string) => api.delete<{ ok: true }>(`/tasks/${encodeURIComponent(id)}`),
}

// ============================================================
// Dashboard API
// ============================================================
export type DashboardKpi = {
  newLeads: number
  followUps: number
  followUpsOverdue: number
  awaitingDocs: number
  awaitingDocsOverdue: number
  submitted: number
  submittedThisWeek: number
  approved: number
  approvedThisWeek: number
  settledCount: number
  settledValueMonth: number
  trendNewLeads: number
}

export type DashboardSettlementMonth = {
  month: string
  label: string
  count: number
  total: number
}

export type DashboardTopLender = {
  lender: string
  slug: string | null
  logoUrl: string | null
  count: number
  total: number
}

export type DashboardTopAutomation = {
  id: string
  name: string
  status: string
  activeCount: number
  waitingCount: number
}

export type DashboardMyPerformance = {
  settlementsCount: number
  settlementsCountPrev: number
  settlementsValue: number
  settlementsValuePrev: number
  conversionRate: number
  avgSettlementDays: number
}

export type DashboardPipelineStage = {
  count: number
  total: number
  recent: Array<{
    id: string
    contactId: string
    dealName: string | null
    loanAmount: number | null
    naf: number | null
    status: DealStatus
    pipelineStage: PipelineStage
    updatedAt: number
    contactBusinessName: string | null
    contactFirstName: string | null
    contactLastName: string | null
    ownerName: string | null
  }>
}

export type DashboardTask = {
  id: string
  title: string
  priority: Priority
  status: TaskStatus
  dueAt: number | null
  contactId: string | null
  dealId: string | null
  contactBusinessName: string | null
  contactFirstName: string | null
  contactLastName: string | null
  dealName: string | null
}

export type DashboardActivity = {
  id: string
  action: string
  entityType: string | null
  entityId: string | null
  metadata: string | null
  createdAt: number
  userId: string | null
  userName: string | null
}

export type DashboardResponse = {
  kpi: DashboardKpi
  pipeline: Record<string, DashboardPipelineStage>
  myTasks: {
    today: DashboardTask[]
    overdue: DashboardTask[]
    upcoming: DashboardTask[]
  }
  activity: DashboardActivity[]
  settlementsChart: DashboardSettlementMonth[]
  topLenders: DashboardTopLender[]
  topAutomations: DashboardTopAutomation[]
  myPerformance: DashboardMyPerformance
}

export const dashboardApi = {
  get: () => api.get<DashboardResponse>("/dashboard"),
}

// ============================================================
// Lenders API
// ============================================================
export type Lender = {
  id: string
  slug: string
  name: string
  displayName: string | null
  legalName: string | null
  logoUrl: string | null
  logoContentType: string | null
  websiteUrl: string | null
  consumerEnabled: boolean
  commercialEnabled: boolean
  financeProducts: string[]
  isActive: boolean
  isFeatured: boolean
  sortOrder: number
  sourceUrl: string | null
  hasLogo: boolean
  updatedAt: number
  createdAt: number
}

export type LenderInput = {
  slug: string
  name: string
  displayName?: string | null
  legalName?: string | null
  websiteUrl?: string | null
  consumerEnabled?: boolean
  commercialEnabled?: boolean
  financeProducts?: string[]
  isFeatured?: boolean
  sortOrder?: number
  isActive?: boolean
}

export type LenderListParams = {
  q?: string
  active?: "true" | "false" | "all"
  featured?: "true" | "false" | "all"
  finance?: "consumer" | "commercial" | "all"
  sort?: "name" | "sort_order" | "updated"
}

export const lendersApi = {
  list: (params: LenderListParams = {}) => {
    const query = new URLSearchParams()
    if (params.q) query.set("q", params.q)
    if (params.active) query.set("active", params.active)
    if (params.featured) query.set("featured", params.featured)
    if (params.finance) query.set("finance", params.finance)
    if (params.sort) query.set("sort", params.sort)
    const suffix = query.toString() ? `?${query}` : ""
    return api.get<{ items: Lender[] }>(`/lenders${suffix}`)
  },
  create: (data: LenderInput) => api.post<{ ok: true; id: string }>("/lenders", data),
  update: (id: string, data: Partial<LenderInput>) =>
    api.patch<{ ok: true }>(`/lenders/${encodeURIComponent(id)}`, data),
  remove: (id: string) =>
    api.delete<{ ok: true; deactivated?: boolean; deleted?: boolean }>(
      `/lenders/${encodeURIComponent(id)}`
    ),
  reorder: (ids: string[]) =>
    api.post<{ ok: true }>("/lenders/reorder", { ids }),
  seed: () =>
    api.post<{ ok: true; added: number; alreadyPresent: number; totalInSeed: number }>(
      "/lenders/seed"
    ),
  uploadLogo: async (id: string, file: File) => {
    const fd = new FormData()
    fd.append("file", file)
    const headers: Record<string, string> = {}
    const token = getCsrfToken()
    if (token) headers["X-CSRF-Token"] = token
    const res = await fetch(`${API_BASE}/lenders/${encodeURIComponent(id)}/logo`, {
      method: "POST",
      body: fd,
      credentials: "include",
      headers,
    })
    const data = (await res.json()) as {
      ok?: boolean
      logoUrl?: string
      contentType?: string
      bytes?: number
      error?: string
      message?: string
    }
    if (!res.ok) {
      throw new ApiError(
        res.status,
        data?.error ?? "upload_failed",
        data?.message ?? "Upload failed"
      )
    }
    return data as { ok: true; logoUrl: string; contentType: string; bytes: number }
  },
  removeLogo: (id: string) =>
    api.delete<{ ok: true }>(`/lenders/${encodeURIComponent(id)}/logo`),
  importZip: async (file: File) => {
    const fd = new FormData()
    fd.append("file", file)
    const headers: Record<string, string> = {}
    const token = getCsrfToken()
    if (token) headers["X-CSRF-Token"] = token
    const res = await fetch(`${API_BASE}/lenders/import-zip`, {
      method: "POST",
      body: fd,
      credentials: "include",
      headers,
    })
    const data = (await res.json()) as {
      ok?: boolean
      processed?: number
      results?: Array<{
        filename: string
        slug: string
        matched: boolean
        lenderId?: string
        contentType?: string
        error?: string
      }>
      stillMissing?: string[]
      error?: string
      message?: string
    }
    if (!res.ok) {
      throw new ApiError(
        res.status,
        data?.error ?? "import_failed",
        data?.message ?? "Import failed"
      )
    }
    return data
  },
  /** Build the URL for a logo (relative to /api). */
  logoUrl: (slug: string) => `${API_BASE}/lenders/${encodeURIComponent(slug)}/logo`,
}

// ============================================================
// Communications API
// ============================================================
export type CommunicationType =
  | "call"
  | "email"
  | "sms"
  | "note"
  | "activity"
  | "lead_submission"
export type CommunicationDirection = "inbound" | "outbound"

export type MediaAttachment = {
  fileName: string
  contentType: string
  storageKey: string
  sizeBytes: number
  originalUrl?: string
}

export type Communication = {
  id: string
  contactId: string
  dealId: string | null
  type: CommunicationType
  direction: CommunicationDirection | null
  subject: string | null
  body: string | null
  durationSeconds: number | null
  recordingUrl: string | null
  aiSummary: string | null
  aiTopics: string | null // JSON stringified array
  aiSentiment: string | null
  aiStatus: "pending" | "done" | "unavailable" | "error" | null
  mediaAttachments: string | null // JSON stringified MediaAttachment[]
  fromAddress: string | null
  toAddress: string | null
  providerName: string | null
  providerRef: string | null
  createdAt: number
  createdBy: string | null
  contactFirstName: string | null
  contactLastName: string | null
  contactBusinessName: string | null
  dealName: string | null
  createdByName: string | null
}

export type CommunicationListParams = {
  contactId?: string
  dealId?: string
  type?: CommunicationType
  page?: number
  pageSize?: number
}

export const communicationsApi = {
  list: (params: CommunicationListParams = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") query.set(k, String(v))
    })
    const suffix = query.toString() ? `?${query}` : ""
    return api.get<{ items: Communication[]; page: number; pageSize: number }>(
      `/communications${suffix}`
    )
  },

  addNote: (data: { contactId: string; dealId?: string | null; body: string }) =>
    api.post<{ ok: true; id: string }>("/communications/notes", data),

  create: (data: {
    contactId: string
    dealId?: string | null
    type: CommunicationType
    direction?: CommunicationDirection | null
    subject?: string | null
    body?: string | null
    durationSeconds?: number | null
    recordingUrl?: string | null
    fromAddress?: string | null
    toAddress?: string | null
  }) => api.post<{ ok: true; id: string }>("/communications", data),

  remove: (id: string) => api.delete<{ ok: true }>(`/communications/${encodeURIComponent(id)}`),

  /** Fetch full email content (from Graph on-demand for synced emails). */
  getEmail: (id: string) =>
    api.get<{
      id: string
      subject: string | null
      from: string | null
      fromName?: string | null
      to?: string[] | string | null
      cc?: string[]
      direction: "inbound" | "outbound" | null
      receivedAt: number
      contentType: "html" | "text"
      body: string
      hasAttachments?: boolean
      webLink?: string | null
      previewOnly: boolean
      error?: string
    }>(`/communications/${encodeURIComponent(id)}/email`),

  /** Send an SMS via the broker's Aircall number. */
  sendSms: (data: {
    contactId: string
    dealId?: string | null
    body?: string
    templateId?: string
    applyMerge?: boolean
    /** Signed public URLs Aircall's servers will fetch to attach as MMS. */
    mediaUrls?: string[]
    /**
     * Metadata for the same media, in the same order as mediaUrls, so the
     * server can record them in our own outbound row and render thumbnails
     * without waiting for Aircall's echo webhook (which is unreliable).
     */
    mediaMeta?: Array<{
      fileName: string
      contentType: string
      sizeBytes: number
      storageKey: string
    }>
  }) =>
    api.post<{ ok: boolean; id?: string; aircallMessageId?: string | number | null; body?: string; error?: string; message?: string }>(
      "/communications/sms/send",
      data
    ),

  /** Click-to-call: rings the broker's phone then bridges to the contact. */
  dialCall: (data: { contactId: string }) =>
    api.post<{ ok: boolean; to?: string; error?: string; message?: string }>(
      "/communications/call/dial",
      data
    ),

  /** Absolute-ish URL for the fresh Aircall recording (server 302s to real URL). */
  recordingUrl: (id: string) =>
    `${API_BASE}/communications/${encodeURIComponent(id)}/recording`,

  /** Absolute-ish URL for an MMS media attachment stream. */
  mediaUrl: (id: string, index: number) =>
    `${API_BASE}/communications/${encodeURIComponent(id)}/media/${index}`,

  /** Upload an outbound MMS file. Returns a public signed URL to hand to Aircall. */
  uploadMms: async (
    file: File
  ): Promise<
    | {
        ok: true
        fileName: string
        contentType: string
        sizeBytes: number
        storageKey: string
        signedUrl: string
        expiresAt: number
      }
    | { ok: false; message: string }
  > => {
    const fd = new FormData()
    fd.append("file", file)
    try {
      const csrf = getCsrfToken() ?? ""
      const res = await fetch(`${API_BASE}/communications/mms/upload`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": csrf },
        body: fd,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
        return { ok: false, message: err.message ?? `HTTP ${res.status}` }
      }
      return (await res.json()) as {
        ok: true
        fileName: string
        contentType: string
        sizeBytes: number
        storageKey: string
        signedUrl: string
        expiresAt: number
      }
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : "Upload failed" }
    }
  },
}

// ============================================================
// Documents API
// ============================================================
export type DocumentListItem = {
  id: string
  contactId: string | null
  dealId: string | null
  filename: string
  displayName: string
  contentType: string
  sizeBytes: number
  category: string | null
  notes: string | null
  deletedAt: number | null
  createdAt: number
  uploadedBy: string | null
  uploaderName: string | null
  contactFirstName: string | null
  contactLastName: string | null
  contactBusinessName: string | null
  dealName: string | null
}

export type DocumentListParams = {
  contactId?: string
  dealId?: string
  category?: string
  includeDeleted?: boolean
  page?: number
  pageSize?: number
}

async function uploadFormData<T>(path: string, form: FormData): Promise<T> {
  const headers = new Headers()
  const csrf = getCsrfToken()
  if (csrf) headers.set("X-CSRF-Token", csrf)
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers,
    body: form,
  })
  const isJson = (res.headers.get("content-type") ?? "").includes("application/json")
  const body = isJson ? await res.json() : null
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? "unknown_error",
      body?.message ?? res.statusText,
      body?.fieldErrors,
      body?.correlationId
    )
  }
  return body as T
}

export const documentsApi = {
  list: (params: DocumentListParams = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") query.set(k, String(v))
    })
    const suffix = query.toString() ? `?${query}` : ""
    return api.get<{ items: DocumentListItem[]; page: number; pageSize: number }>(
      `/documents${suffix}`
    )
  },

  upload: (form: FormData) => uploadFormData<{ ok: true; id: string }>("/documents/upload", form),

  signedUrl: (id: string) =>
    api.post<{ url: string; expiresAt: number; filename: string }>(
      `/documents/${encodeURIComponent(id)}/signed-url`
    ),

  update: (
    id: string,
    data: { displayName?: string; category?: string | null; notes?: string | null }
  ) => api.patch<{ ok: true }>(`/documents/${encodeURIComponent(id)}`, data),

  remove: (id: string) => api.delete<{ ok: true }>(`/documents/${encodeURIComponent(id)}`),
}

// ============================================================
// Templates API
// ============================================================
export type TemplateType = "email" | "sms"

export type TemplateListItem = {
  id: string
  name: string
  type: TemplateType
  category: string | null
  subject: string | null
  body: string
  includeSignature: boolean
  isShared: boolean
  isActive: boolean
  ownerId: string | null
  ownerName: string | null
  createdAt: number
  updatedAt: number
}

export type TemplateInput = {
  name: string
  type: TemplateType
  category?: string | null
  subject?: string | null
  body: string
  includeSignature?: boolean
  isShared?: boolean
  isActive?: boolean
}

export type MergeField = { key: string; label: string }

export type EmailSignature = {
  userId: string
  html: string
  plainText: string | null
  updatedAt: number
}

export const templatesApi = {
  list: (type?: TemplateType) => {
    const query = type ? `?type=${type}` : ""
    return api.get<{ items: TemplateListItem[] }>(`/templates${query}`)
  },

  get: (id: string) =>
    api.get<{ template: TemplateListItem }>(`/templates/${encodeURIComponent(id)}`),

  create: (data: TemplateInput) => api.post<{ ok: true; id: string }>("/templates", data),

  update: (id: string, data: Partial<TemplateInput>) =>
    api.patch<{ ok: true }>(`/templates/${encodeURIComponent(id)}`, data),

  remove: (id: string) => api.delete<{ ok: true }>(`/templates/${encodeURIComponent(id)}`),

  render: (id: string, data: { contactId?: string; dealId?: string }) =>
    api.post<{ subject: string | null; body: string; type: TemplateType }>(
      `/templates/${encodeURIComponent(id)}/render`,
      data
    ),

  mergeFields: () => api.get<{ fields: MergeField[] }>("/templates/merge-fields"),

  getSignature: () => api.get<{ signature: EmailSignature | null }>("/templates/-/signature"),

  saveSignature: (html: string, plainText?: string | null) =>
    api.put<{ ok: true }>("/templates/-/signature", { html, plainText }),
}

// ============================================================
// Reports API
// ============================================================
export type ReportSummary = {
  settledThisMonth: { count: number; value: number }
  settledLastMonth: { count: number; value: number }
  pipelineValue: { count: number; value: number }
  conversionRate: number
}

export type BrokerReportRow = {
  brokerId: string | null
  brokerName: string | null
  totalDeals: number
  settled: number
  settledValue: number
  pipelineValue: number
}

export type TopCategoryRow = {
  category: string | null
  count: number
  value: number
}

export type MonthlyRow = {
  month: string
  count: number
  value: number
}

export type ReportFilters = {
  brokerId?: string
  financeType?: FinanceType
}

export type ComplianceReport = {
  windowDays: number
  windowStart: number
  totals: Array<{
    channel: "sms" | "email"
    classification: "direct" | "marketing"
    outcome: "sent" | "skipped" | "failed"
    count: number
  }>
  skipCodes: Array<{
    channel: "sms" | "email"
    classification: "direct" | "marketing"
    skipCode: string | null
    count: number
  }>
  contactSnapshot: {
    total: number
    smsSubscribed: number
    smsUnsubscribed: number
    emailSubscribed: number
    emailUnsubscribed: number
    doNotContact: number
  }
  recentOptOuts: Array<{
    id: string
    contactId: string
    fieldName: string
    newValue: string | null
    changeSource: string
    changeReason: string | null
    inboundMessageText: string | null
    changedAt: number
  }>
}

export const reportsApi = {
  summary: (filters: ReportFilters = {}) => {
    const query = new URLSearchParams()
    if (filters.brokerId) query.set("brokerId", filters.brokerId)
    if (filters.financeType) query.set("financeType", filters.financeType)
    const suffix = query.toString() ? `?${query}` : ""
    return api.get<ReportSummary>(`/reports/summary${suffix}`)
  },
  brokers: () => api.get<{ brokers: BrokerReportRow[] }>("/reports/brokers"),
  topCategories: () => api.get<{ categories: TopCategoryRow[] }>("/reports/top-categories"),
  monthly: () => api.get<{ months: MonthlyRow[] }>("/reports/monthly"),
  compliance: (days?: number) => {
    const suffix = days ? `?days=${days}` : ""
    return api.get<ComplianceReport>(`/reports/preferences${suffix}`)
  },
}

// ============================================================
// Integrations API
// ============================================================
export type IntegrationStatus = {
  orgId: string
  aircallWebhookUrl: string
  org: {
    aircall: null | {
      provider: "aircall"
      isActive: boolean
      displayName: string | null
      updatedAt: number
    }
    outlook: null | {
      provider: "outlook"
      isActive: boolean
      displayName: string | null
      updatedAt: number
    }
  }
  user: {
    outlook: null | {
      provider: "outlook"
      isActive: boolean
      externalAccountId: string | null
      lastSyncAt: number | null
      lastSyncError: string | null
      updatedAt: number
    }
    aircall: null | { provider: "aircall"; isActive: boolean; externalAccountId: string | null }
  }
  envConfigured: {
    aircall: { webhookSecret: boolean }
    outlook: { clientId: boolean; tenantId: boolean }
  }
}

export type UnmatchedEmail = {
  id: string
  subject: string | null
  fromAddress: string | null
  fromName: string | null
  bodyPreview: string | null
  receivedAt: number
  createdAt: number
}

export const integrationsApi = {
  status: () => api.get<IntegrationStatus>("/integrations/status"),

  connectAircall: (data: { apiId: string; apiToken: string; workspaceName?: string }) =>
    api.post<{ ok: true }>("/integrations/aircall", data),

  disconnectAircall: () => api.delete<{ ok: true }>("/integrations/aircall"),

  testAircall: () =>
    api.post<{ ok: boolean; message?: string; userCount?: string }>("/integrations/aircall/test"),

  syncAircallUsers: () =>
    api.post<{
      ok: true
      totals: {
        aircallUsers: number
        matched: number
        unmatchedAircall: number
        unmatchedBrokers: number
      }
      matched: Array<{
        crmUserId: string
        crmEmail: string
        aircallUserId: number
        aircallNumberId: number | null
        aircallNumberE164: string | null
      }>
      unmatchedAircall: Array<{
        aircallUserId: number
        aircallEmail: string | null
        aircallName: string | null
      }>
      unmatchedBrokers: Array<{
        id: string
        email: string
        fullName: string
      }>
    }>("/integrations/aircall/sync-users"),

  backfillAircallAi: (opts: { limit?: number; force?: boolean } = {}) =>
    api.post<{
      ok: true
      attempted: number
      done: number
      unavailable: number
      errored: number
    }>("/integrations/aircall/backfill-ai", {
      limit: opts.limit ?? 20,
      force: opts.force ?? false,
    }),

  outlookAuthorize: () => api.get<{ url: string }>("/integrations/outlook/authorize"),

  outlookSync: () =>
    api.post<{
      userId: string
      fetched: number
      matched: number
      unmatched: number
      skipped: number
      error?: string
    }>("/integrations/outlook/sync"),

  disconnectOutlook: () => api.delete<{ ok: true }>("/integrations/outlook"),

  outlookUnmatched: () =>
    api.get<{ items: UnmatchedEmail[] }>("/integrations/outlook/unmatched"),

  linkUnmatched: (id: string, contactId: string) =>
    api.post<{ ok: true }>(`/integrations/outlook/unmatched/${encodeURIComponent(id)}/link`, {
      contactId,
    }),
}

// ============================================================
// Audit Log API
// ============================================================
export type AuditLogEntry = {
  id: string
  action: string
  entityType: string | null
  entityId: string | null
  ipAddress: string | null
  userAgent: string | null
  metadata: string | null
  correlationId: string | null
  createdAt: number
  userId: string | null
  userName: string | null
  userEmail: string | null
}

export type AuditListParams = {
  q?: string
  action?: string
  entityType?: string
  userId?: string
  from?: number
  to?: number
  page?: number
  pageSize?: number
}

export const auditApi = {
  list: (params: AuditListParams = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") query.set(k, String(v))
    })
    const suffix = query.toString() ? `?${query}` : ""
    return api.get<{
      items: AuditLogEntry[]
      total: number
      page: number
      pageSize: number
    }>(`/audit${suffix}`)
  },

  actions: () => api.get<{ actions: string[] }>("/audit/actions"),
}

// ============================================================
// Import API
// ============================================================
export type ImportRow = {
  firstName: string
  lastName: string
  email?: string | null
  mobile?: string | null
  businessName?: string | null
  abn?: string | null
  industry?: string | null
  source?: string | null
  addressSuburb?: string | null
  addressState?: string | null
  addressPostcode?: string | null
  notes?: string | null
}

export type ImportResult = {
  total: number
  created: number
  skipped: number
  errors: Array<{ row: number; reason: string }>
}

export type DealImportRow = {
  brokerName?: string | null
  brokerEmail?: string | null
  lender?: string | null
  appType?: string | null
  loanType?: string | null
  term?: string | number | null
  writingRate?: string | number | null
  remittanceDate?: string | null
  termEndDate?: string | null
  settledDate?: string | null
  naf?: string | number | null
  balloon?: string | number | null
  commission?: string | number | null
  businessName?: string | null
  businessAbn?: string | null
  businessType?: string | null
  isAssetBacked?: string | boolean | number | null
  hasInvestmentProperty?: string | boolean | number | null
  applicantTitle?: string | null
  applicantFirstName: string
  applicantLastName: string
  applicantEmail?: string | null
  applicantMobile?: string | null
  applicantDob?: string | null
  applicantAddress?: string | null
  applicant2Title?: string | null
  applicant2FirstName?: string | null
  applicant2LastName?: string | null
  applicant2Email?: string | null
  applicant2Mobile?: string | null
}

export type DealImportResult = {
  total: number
  contactsCreated: number
  contactsMatched: number
  dealsCreated: number
  errors: Array<{ row: number; reason: string }>
}

export const importApi = {
  contacts: (rows: ImportRow[], skipDuplicates = true, batchSource?: string) =>
    api.post<ImportResult>("/import/contacts", { rows, skipDuplicates, batchSource }),
  deals: (rows: DealImportRow[], batchSource?: string) =>
    api.post<DealImportResult>("/import/deals", { rows, batchSource }),
}

// ============================================================
// Global Search API
// ============================================================
export type SearchContactHit = {
  id: string
  firstName: string
  lastName: string
  businessName: string | null
  email: string | null
  mobile: string | null
  abn: string | null
  status: ContactStatus
  ownerName: string | null
}

export type SearchDealHit = {
  id: string
  contactId: string
  dealName: string | null
  lender: string | null
  loanAmount: number | null
  naf: number | null
  status: DealStatus
  pipelineStage: PipelineStage
  contactFirstName: string | null
  contactLastName: string | null
  contactBusinessName: string | null
}

export type SearchTaskHit = {
  id: string
  title: string
  status: TaskStatus
  priority: Priority
  dueAt: number | null
  contactId: string | null
  contactFirstName: string | null
  contactLastName: string | null
  contactBusinessName: string | null
}

export type SearchResults = {
  query: string
  contacts: SearchContactHit[]
  deals: SearchDealHit[]
  tasks: SearchTaskHit[]
}

export const searchApi = {
  query: (q: string) =>
    api.get<SearchResults>(`/search?q=${encodeURIComponent(q)}`),
}

// ============================================================
// Automations API
// ============================================================
export type AutomationStatus = "draft" | "active" | "paused" | "archived"

export type AutomationCategory =
  | "lead_nurture"
  | "balloon"
  | "birthday"
  | "settlement"
  | "re_engagement"
  | "compliance"
  | "internal"
  | "other"

export type AutomationListItem = {
  id: string
  name: string
  description: string | null
  status: AutomationStatus
  category: AutomationCategory | null
  smsEnabled: boolean
  emailEnabled: boolean
  workflow: string // JSON string of AutomationWorkflow
  enrolmentCount: number
  successCount: number
  failureCount: number
  activatedAt: number | null
  createdAt: number
  updatedAt: number
}

// Communication classification (Phase 3)
// NOTE: `CommunicationType` (call/email/sms/note/activity) already exists
// above for communications-log entries — this is a separate concept for the
// automation-level compliance classification, so it gets a distinct name.
export type AutomationCommunicationType = "direct" | "marketing" | "mixed"
export type StepClassification = "direct" | "marketing"
export type OperationalReason =
  | "responding_to_enquiry"
  | "active_finance_application"
  | "document_request"
  | "appointment_reminder"
  | "approval_update"
  | "settlement_update"
  | "client_requested_information"
  | "current_service_request"
  | "other"

export const OPERATIONAL_REASON_LABELS: Record<OperationalReason, string> = {
  responding_to_enquiry: "Responding to a client enquiry",
  active_finance_application: "Active finance application",
  document_request: "Document request",
  appointment_reminder: "Appointment reminder",
  approval_update: "Approval update",
  settlement_update: "Settlement update",
  client_requested_information: "Client requested information",
  current_service_request: "Current service request",
  other: "Other operational reason",
}

export type AutomationWorkflow = {
  version: number
  trigger: { kind: string; params?: Record<string, unknown> }
  conditions: Array<{ field: string; op: string; value?: unknown }>
  exitConditions: Array<{ field: string; op: string; value?: unknown }>
  steps: Array<Record<string, unknown> & { kind: string; id: string; label?: string }>
  maxSteps: number
  communicationType?: AutomationCommunicationType
  operationalReason?: OperationalReason
  operationalReasonNotes?: string
}

export type AutomationDetail = AutomationListItem & {
  workflow: AutomationWorkflow
  prompt: string | null
  aiNotes: string | null
}

export type AutomationRun = {
  id: string
  status: string
  currentStepIndex: number
  contactId: string | null
  dealId: string | null
  startedAt: number
  completedAt: number | null
  nextRunAt?: number | null
  error: string | null
  contactFirstName?: string | null
  contactLastName?: string | null
  contactBusinessName?: string | null
  dealName?: string | null
  // Reference dates from the linked deal (for date-based triggers).
  dealSettledDate?: string | null
  dealLostDate?: string | null
  dealTermEndDate?: string | null
  /** Per-run send stats from automation_send_log — Phase 6.5 */
  sendStats?: {
    sent: number
    skipped: number
    failed: number
    optOutSkip: boolean
  }
}

export type ComposeResult = {
  ok: true
  workflow: AutomationWorkflow
  aiNotes: string
  prompt: string
} | {
  ok: false
  error: string
  message: string
}

export const automationsApi = {
  compose: (prompt: string) =>
    api.post<ComposeResult>("/automations/compose", { prompt }),

  list: () => api.get<{ items: AutomationListItem[] }>("/automations"),

  get: (id: string) =>
    api.get<{ automation: AutomationDetail; runs: AutomationRun[] }>(
      `/automations/${encodeURIComponent(id)}`
    ),

  create: (data: {
    name: string
    description?: string | null
    workflow: AutomationWorkflow
    prompt?: string | null
    aiNotes?: string | null
    category?: AutomationCategory
    smsEnabled?: boolean
    emailEnabled?: boolean
  }) => api.post<{ ok: true; id: string }>("/automations", data),

  update: (
    id: string,
    data: {
      name?: string
      description?: string | null
      workflow?: AutomationWorkflow
      category?: AutomationCategory
      smsEnabled?: boolean
      emailEnabled?: boolean
    }
  ) => api.patch<{ ok: true }>(`/automations/${encodeURIComponent(id)}`, data),

  activate: (id: string) =>
    api.post<{ ok: true }>(`/automations/${encodeURIComponent(id)}/activate`),

  pause: (id: string, status: "paused" | "archived" = "paused") =>
    api.post<{ ok: true }>(`/automations/${encodeURIComponent(id)}/pause`, { status }),

  remove: (id: string) => api.delete<{ ok: true }>(`/automations/${encodeURIComponent(id)}`),

  previewEnrol: (payload: {
    contactId: string
    dealId?: string
    automationIds: string[]
  }) =>
    api.post<{
      ok: true
      previews: Array<{
        automationId: string
        name: string | null
        triggerKind?: string
        verdict:
          | "fire_today"
          | "schedule"
          | "past"
          | "below_go_live_floor"
          | "missing_reference_data"
          | "automation_not_found"
          | "automation_not_active"
          | "unknown_trigger"
          | "event_driven"
        scheduledFor?: string
        reason?: string
      }>
    }>(`/automations/preview-enrol`, payload),

  enroll: (
    id: string,
    target: {
      contactId?: string
      dealId?: string
      /** DEPRECATED (incident 2026-08-06). Server ignores; kept for wire-compat. */
      overridePause?: boolean
      /** True = broker confirmed they're OK with a future-dated enrol. */
      confirmFuture?: boolean
    }
  ) =>
    api.post<{
      ok: true
      enrolled: number
      /** When the run was queued for a future date, this is the ISO date it will fire (Brisbane). */
      scheduledFor?: string
      /** True when the enrolment produced a future-dated run. */
      scheduled?: boolean
      /** When enrolled=0, hints at why. Used by the bulk dialog to show accurate labels. */
      skipReason?:
        | "no_matching_automation"
        | "already_running"
        | "import_reference_date_block"
        | "conditions_not_met"
        | "trigger_params_mismatch"
        | "trigger_date_in_past"
        | "below_go_live_floor"
        | "missing_reference_data"
        | "unknown_trigger"
    }>(`/automations/${encodeURIComponent(id)}/enroll`, target),

  testSendSms: (data: {
    toPhone: string
    body: string
    classification?: "direct" | "marketing"
    suppressOptOutWording?: boolean
  }) =>
    api.post<{
      ok: true
      aircallMessageId: string | number | null
      renderedBody: string
    } | { ok: false; error: string }>("/automations/test-send-sms", data),

  testSendEmail: (data: {
    toEmail: string
    subject: string
    body: string
    appendSignature?: boolean
    attachmentIds?: string[]
    classification?: "direct" | "marketing"
    suppressUnsubscribeFooter?: boolean
  }) =>
    api.post<{
      ok: true
      renderedSubject: string
      renderedBody: string
      attachmentCount?: number
    } | { ok: false; error: string }>("/automations/test-send-email", data),

  uploadAttachment: async (file: File) => {
    const form = new FormData()
    form.append("file", file)
    const res = await fetch(`${API_BASE}/automations/attachments/upload`, {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken() ?? "",
      },
      body: form,
    })
    if (!res.ok) {
      const text = await res.text()
      throw new Error(text || `Upload failed (${res.status})`)
    }
    return res.json() as Promise<{
      id: string
      filename: string
      contentType: string
      sizeBytes: number
      createdAt: number
    }>
  },

  listAttachments: (ids: string[]) =>
    api.get<{
      items: Array<{
        id: string
        filename: string
        contentType: string
        sizeBytes: number
        createdAt: number
      }>
    }>(`/automations/attachments?ids=${encodeURIComponent(ids.join(","))}`),

  deleteAttachment: (id: string) =>
    api.delete<{ ok: boolean }>(`/automations/attachments/${id}`),

  /**
   * Upload an image to embed inline in an email body. Returns a public URL
   * that email clients can fetch when rendering the email. Use this in the
   * RichTextEditor onImageUpload handler so images don't get inlined as
   * base64 (which blows past the 200k body char limit).
   */
  uploadInlineImage: async (file: File) => {
    const form = new FormData()
    form.append("file", file)
    const res = await fetch(`${API_BASE}/automations/inline-images/upload`, {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken() ?? "",
      },
      body: form,
    })
    if (!res.ok) {
      const text = await res.text()
      throw new Error(text || `Image upload failed (${res.status})`)
    }
    return res.json() as Promise<{
      id: string
      url: string
      sizeBytes: number
      contentType: string
    }>
  },

  runDue: () =>
    api.post<{
      ok: true
      processed: number
      results: Array<{
        runId: string
        status: string
        stepsExecuted: number
        error?: string
      }>
    }>("/automations/run-due"),

  runs: (id: string) =>
    api.get<{ items: AutomationRun[] }>(`/automations/${encodeURIComponent(id)}/runs`),

  cancelRun: (automationId: string, runId: string) =>
    api.post<{ ok: true; alreadyTerminal?: boolean }>(
      `/automations/${encodeURIComponent(automationId)}/runs/${encodeURIComponent(runId)}/cancel`
    ),

  bulkCancelRuns: (
    automationId: string,
    body: { runIds?: string[]; all?: boolean }
  ) =>
    api.post<{ ok: true; cancelled: number }>(
      `/automations/${encodeURIComponent(automationId)}/runs/bulk-cancel`,
      body
    ),
}
